import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';

export interface InstructionsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  projectName: string;
  tempInstructions: string;
  onInstructionsChange: (value: string) => void;
  onSave: () => void;
  isSaving: boolean;
}

export function InstructionsModal({
  isOpen,
  onOpenChange,
  projectName,
  tempInstructions,
  onInstructionsChange,
  onSave,
  isSaving,
}: InstructionsModalProps) {
  const { t } = useTranslation();
  const [, forceUpdate] = useState(0);
  const [originalInstructions, setOriginalInstructions] = useState<string>('');

  // Update original instructions when modal opens
  useEffect(() => {
    if (isOpen) {
      setOriginalInstructions(tempInstructions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen, not tempInstructions!

  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate(prev => prev + 1);
    };

    i18nInstance.on('languageChanged', handleLanguageChange);

    return () => {
      i18nInstance.off('languageChanged', handleLanguageChange);
    };
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t('project.setProjectInstructions')}
          </DialogTitle>
          <p className="text-sm text-gray-600 mt-2">
            Provide JDIX with relevant instructions and information for chats
            within <span className="font-medium">{projectName}</span>. This will
            work alongside user preferences and the selected style in a chat.
          </p>
        </DialogHeader>
        <div className="px-6 py-4">
          <textarea
            value={tempInstructions}
            onChange={e => onInstructionsChange(e.target.value)}
            placeholder={t('project.instructionsPlaceholder')}
            className="w-full h-64 p-4 text-sm border border-gray-400 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-blue-300 bg-white"
            autoFocus
          />
        </div>
        <DialogFooter className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
            className="px-4 py-2"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={onSave}
            disabled={
              isSaving ||
              tempInstructions.trim() === originalInstructions.trim()
            }
            className="px-4 py-2 bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50"
          >
            {isSaving ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                {t('common.saving')}
              </span>
            ) : (
              t('project.saveInstructions')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default InstructionsModal;
